import { useState } from 'react'
import { KeyRound } from 'lucide-react'
import { adminMutate } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { fmtDateHeure, messageEchecMutation, type JetonSonde, type ResultatTestJeton } from './api.js'

/**
 * UN JETON DE GESTION CHEZ SONDE (`ssk_…`) — deux panneaux sur la page
 * Intégrations › Sonde : celui de l'API (le relais des écrans, gestes humains)
 * et celui du worker (miroir, flux d'alertes, instantanés).
 *
 * « Tester » le présente à Sonde SANS l'enregistrer et dit à quel jeton il
 * correspond ; « Enregistrer » le range au coffre de Gestion (confirmation
 * forte). Le champ est toujours vide : le jeton n'est jamais réaffiché.
 */

const NOMS: Record<JetonSonde, { titre: string; role: string; soi: string; autre: string }> = {
  api: { titre: 'Jeton de l’API', role: 'les écrans de Gestion et les gestes humains (clés, réglages, acquittements)', soi: 'celui de l’API', autre: 'celui du worker' },
  worker: { titre: 'Jeton du worker', role: 'le miroir, le flux d’alertes et les instantanés de bascule', soi: 'celui du worker', autre: 'celui de l’API' },
}

export function JetonSondePanel({ kind, etat, peutModifier, onEnregistre }: {
  kind: JetonSonde
  etat: { set: boolean; updated_at: string | null } | null
  peutModifier: boolean
  onEnregistre: () => void
}) {
  const confirm = useConfirm()
  const n = NOMS[kind]
  // Toujours vide au départ : la valeur ne vient JAMAIS du serveur.
  const [jeton, setJeton] = useState('')
  const [essai, setEssai] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [verdict, setVerdict] = useState<{ ton: 'ok' | 'ko' | 'doute' | 'gris'; texte: string; pour: string } | null>(null)
  const [apres, setApres] = useState<{ ton: 'ok' | 'ko' | 'gris'; texte: string } | null>(null)

  const valeur = jeton.trim()
  const tropCourt = valeur.length < 16
  const verdictCourant = verdict && verdict.pour === valeur ? verdict : null

  const tester = async () => {
    if (tropCourt) return
    setEssai(true); setApres(null)
    try {
      const r = await adminMutate<ResultatTestJeton & { error?: string }>('POST', `/api/v1/supervision/tokens/${kind}/test`, { token: valeur })
      if (r.status === 200 && typeof r.data.ok === 'boolean') {
        const d = r.data
        if (d.kind_matches === false) {
          setVerdict({ ton: 'doute', texte: `Ce jeton est ${n.autre}, pas ${n.soi}. Collez-le dans l’autre panneau.`, pour: valeur })
        } else if (d.ok) {
          const qui = d.token_name ? ` (« ${d.token_name} »${d.scopes?.length ? ` : ${d.scopes.join(', ')}` : ''})` : ''
          setVerdict({ ton: 'ok', texte: `Sonde reconnaît ce jeton${qui}. Il n’est pas encore enregistré.`, pour: valeur })
        } else {
          setVerdict({ ton: 'ko', texte: d.message || 'Sonde refuse ce jeton.', pour: valeur })
        }
      } else {
        setVerdict({ ton: 'gris', texte: messageEchecMutation(r.status, r.data, 'l’essai n’a pas eu lieu'), pour: valeur })
      }
    } catch {
      setVerdict({ ton: 'gris', texte: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: valeur })
    } finally { setEssai(false) }
  }

  const enregistrer = async () => {
    if (tropCourt) return
    if (verdictCourant?.ton !== 'ok') {
      const ok = await confirm({
        title: 'Enregistrer un jeton qui n’a pas été reconnu ?',
        message: verdictCourant
          ? `Le dernier essai n’a pas abouti : ${verdictCourant.texte} S’il est faux, Gestion ne pourra plus parler à Sonde pour ${n.role}.`
          : `Ce jeton n’a pas été essayé. S’il est faux, Gestion ne pourra plus parler à Sonde pour ${n.role}. Mieux vaut cliquer d’abord sur « Tester ».`,
        variant: 'sensitive',
        confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<{ ok?: boolean; error?: string; message?: string }>('PUT', `/api/v1/supervision/tokens/${kind}`, { token: valeur })
      if (r.status >= 200 && r.status < 300) {
        // Vidé AVANT tout le reste : le secret ne séjourne pas dans la page.
        setJeton(''); setVerdict(null)
        setApres({ ton: 'ok', texte: 'Jeton rangé au coffre de Gestion. Il ne sera jamais réaffiché.' })
        onEnregistre()
        return
      }
      setApres({ ton: r.status === 503 ? 'gris' : 'ko', texte: messageEchecMutation(r.status, r.data, 'le jeton n’a pas été enregistré') })
    } catch {
      setApres({ ton: 'gris', texte: 'Gestion n’a pas répondu : relisez la page avant de réessayer.' })
    } finally { setEnvoi(false) }
  }

  return (
    <section className="lc-panel cle-panel" aria-labelledby={`sv-jeton-${kind}`}>
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id={`sv-jeton-${kind}`}><KeyRound size={16} aria-hidden="true" /> {n.titre}</h2>
        <span className={`lc-pill ${etat?.set ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{etat === null ? '—' : etat.set ? 'Enregistré' : 'Absent'}</span>
      </header>
      <div className="lc-panel__body cle-corps">
        <p className="lc-hint cle-intro">Sert à {n.role}.</p>
        <dl className="cle-faits">
          <div>
            <dt>Jeton actuel</dt>
            <dd>{etat === null ? '—' : etat.set ? `Enregistré${etat.updated_at ? ` le ${fmtDateHeure(etat.updated_at)}` : ''}` : 'Aucun jeton : Gestion ne peut pas encore parler à Sonde.'}</dd>
          </div>
        </dl>
        {peutModifier ? (
          <div className="cle-saisie">
            <label className="cle-champ">
              <span>Nouveau jeton</span>
              <input
                className="lc-input" type="password" autoComplete="new-password" spellCheck={false}
                name={`sonde-jeton-${kind}`} value={jeton} placeholder="ssk_…"
                aria-label={`Nouveau ${n.titre.toLowerCase()}`}
                onChange={(e) => { setJeton(e.target.value); setApres(null) }}
              />
            </label>
            <div className="cle-actions">
              <button type="button" className="lc-btn" disabled={tropCourt || essai || envoi} onClick={() => { void tester() }}>
                {essai ? 'Essai en cours…' : 'Tester'}
              </button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={tropCourt || essai || envoi} onClick={() => { void enregistrer() }}>
                {envoi ? 'Enregistrement…' : 'Enregistrer le jeton'}
              </button>
            </div>
            {verdictCourant ? (
              <p role="status" className={`cle-verdict${verdictCourant.ton === 'gris' ? '' : ` cle-verdict--${verdictCourant.ton}`}`}>{verdictCourant.texte}</p>
            ) : null}
          </div>
        ) : (
          <p className="lc-hint">Seul un compte autorisé à gérer les clés de Sonde peut remplacer ce jeton.</p>
        )}
        {apres ? <p role="status" className={`cle-verdict${apres.ton === 'gris' ? '' : ` cle-verdict--${apres.ton}`}`}>{apres.texte}</p> : null}
      </div>
    </section>
  )
}
